// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import '../styles/platform-terms.css';

/**
 * COB-580 — the Cobault Platform Terms, "Standard Shared Vaults": the
 * approved legal text, served as a static **browsewrap** page.
 *
 * **v1.1 (COB-592).** v1.0 defined the "Platform" as cobault.io *and*
 * wrap.cobault.io and called the pair a "web application". After COB-587
 * cobault.io serves the company page, so the description was wrong, and
 * clause 1.5 made a visit to a marketing page an act of acceptance. v1.1
 * splits the definition — web application at wrap, informational website at
 * cobault.io — and narrows 1.5's acceptance trigger to the web application.
 * The defined term stays broad on purpose: clause 3.2 relies on it, because
 * the supported-jurisdictions list is published on the marketing pages and
 * nowhere in the SPA. Narrowing the term would leave 3.2 pointing at
 * nothing. Nothing a user is bound by widened, so this is not a "material
 * change" under 17.2 — no 30-day notice, no re-acceptance.
 *
 * **v1.2 (COB-520).** Phase-2 opening of the rollout: clause 3.2's Supported
 * Jurisdictions extend to the United Kingdom and the Member States of the
 * European Union, in step with the availability list on the marketing pages
 * and the WAF/geofence allowlists. Widening eligibility binds no existing
 * user to anything new, so as with v1.1 this is not a "material change"
 * under 17.2 — no 30-day notice, no re-acceptance. Acceptance slots already
 * recorded keep the version they accepted (COB-512, migration 0074).
 *
 * **v1.3 (COB-518 phase 3).** Phase-3 opening of the rollout: clause 3.2's
 * Supported Jurisdictions extend to the United States of America — the fifty
 * States and the District of Columbia; the territories are excluded — in
 * step with the availability list on the marketing pages and the
 * WAF/geofence allowlists. Clause 3.4 names the sanctions authorities
 * (Australia, the United Nations, the United Kingdom, the European Union and
 * the United States) and restates at the warranty the clause 9 prohibition
 * on using the Platform to evade sanctions; it is the sanctions note's S1(i)
 * variant — no relay-decline sentence — and creates no veto over a signed
 * transaction (any participant may still broadcast, clause 6.2). Clauses
 * 15.7 (United Kingdom and European Union consumer lines — the phase-2 P9
 * annex) and 15.8 (United States consumer line) are added after 15.6.
 * Non-material under 17.2 on the same reasoning as v1.1 and v1.2: widening
 * eligibility binds no existing user to anything new; 15.7 and 15.8 add
 * protections for users and remove nothing; 3.4 restates a warranty every
 * user already gave in substance ("any other authority whose sanctions
 * apply to us") and names the authorities — the "will not use the Platform
 * to send Bitcoin to … any such person" limb is the existing clause 9
 * prohibition restated at the warranty, not a new obligation. No 30-day
 * notice, no re-acceptance. Acceptance slots already recorded keep the
 * version they accepted (COB-512, migration 0074).
 *
 * These are Bluemetal Services Pty Ltd's terms for the standard shared-vault
 * platform — every signing key is held by a participant, Cobault holds none.
 * They replace the pre-GA placeholder, which still described the 2-of-3 yield
 * product (user + product provider + independent verifier) and carried a
 * "pending legal review" banner. Clause 1.4 now says explicitly that yield
 * products are not offered under these Terms.
 *
 * **Browsewrap, no acceptance record.** Clauses 1.5 and 17.2 contemplate a
 * recorded, versioned acceptance; the Platform does not record one yet. The
 * server-side Platform-Terms acceptance gate is COB-512. Until it lands, use
 * of the web application is the acceptance and this page is the published
 * current version — hence the version marker, which is what makes clauses 1.5 and
 * 17.1 ("each version carries a version number and an effective date"; "the
 * current version is always available at wrap.cobault.io/platform-terms")
 * true of the published page.
 *
 * **Colophon edit without a bump (2026-08-31, COB-515).** The colophon's
 * registered-office line dropped "c/o Flinders Financial" — a third-party
 * firm's name inside our address reads as another operating entity. The
 * colophon sits below the terms body and is page furniture, not a term, so
 * v1.1 stands. Clause 20.5's postal address deliberately keeps the "c/o"
 * routing — that line is how notice by post actually reaches Bluemetal.
 *
 * The ProductCo product terms + risk disclosure remain a separate, versioned,
 * explicitly-accepted document served through the acceptance modal
 * (`terms-modal.tsx`) and gated on yield-vault creation. Its version constant
 * (productco-kyc's `CURRENT_TERMS_VERSION`, COB-397) is a *different*
 * document and must never be bumped for a change to this page.
 *
 * Static bundle content: unauthenticated, no data fetching, no dependencies,
 * linked from the layout footer.
 */

/**
 * Version of the published Platform Terms. COB-512 (server-side acceptance
 * record) should import this rather than re-declare the string, so the
 * published page and the recorded version can never drift.
 */
export const PLATFORM_TERMS_VERSION = '1.3';

/** Effective date of {@link PLATFORM_TERMS_VERSION}, as published. */
export const PLATFORM_TERMS_EFFECTIVE_DATE = '12 September 2026';

/** The version marker required by clauses 1.5 and 17.1. */
const VERSION_MARKER = `Version ${PLATFORM_TERMS_VERSION} · Effective ${PLATFORM_TERMS_EFFECTIVE_DATE}`;

/**
 * Clause 8.1's risk list, as data.
 *
 * COB-512 shows these risks on the two consent surfaces that put a signing
 * key on a vault — the claim-link landing and the create wizard's confirm
 * step — and clause 8 below renders the same array. One source, so the
 * disclosure a user is shown and the document they accept can never say
 * different things. Body text is plain prose by construction: nothing here
 * carries markup, so each renderer can lay it out to suit its surface.
 */
export const PLATFORM_TERMS_RISKS: ReadonlyArray<{
  /** Short bolded lead-in, ending in a full stop ('Key loss.'). */
  readonly title: string;
  /** The risk itself, one or more sentences of plain prose. */
  readonly body: string;
}> = [
  {
    title: 'Quorum freeze.',
    body:
      'If a Shared Vault requires every participant to sign (m equals n), the permanent loss of any single key permanently freezes the Bitcoin in it. A vault where m is less than n can survive the loss of up to n minus m keys, and no more. Choose the configuration deliberately.',
  },
  {
    title: 'Key loss.',
    body:
      'If you lose your recovery phrase, your hardware wallet, or access to the sign-in method that holds your embedded wallet, your signing key is gone. Cobault cannot recover it. No one can.',
  },
  {
    title: 'Co-participant risk.',
    body:
      'Any m participants can sign a transaction without the others. If you are not part of the quorum, Bitcoin can be moved without your consent. The safety of the vault depends on the honesty and competence of the people you invite or accept claim links from.',
  },
  {
    title: 'No custodian, no reversal, no compensation.',
    body:
      'There is no custodian, no chargeback, no ability to reverse a transaction, and no deposit guarantee, investor compensation scheme or insurance covering Bitcoin in a Shared Vault.',
  },
  {
    title: 'Public and permanent ledger.',
    body:
      'All transactions are recorded permanently on the public Bitcoin blockchain. Transaction data reveals the vault addresses, amounts and which keys signed. Blockchain records cannot be edited or erased by Cobault.',
  },
  {
    title: 'Volatility.',
    body:
      'The value of Bitcoin can change rapidly and materially. You may lose all of the value you hold.',
  },
  {
    title: 'Software risk.',
    body:
      'The Platform and the third-party software it depends on may contain defects. The Platform was the subject of an independent security assessment in 2026; no assessment can guarantee the absence of defects. Verify addresses and transaction details independently and do not rely solely on the Platform.',
  },
  {
    title: 'Availability and recovery.',
    body:
      'The Platform may be unavailable, suspended or discontinued. Your ability to spend from a Shared Vault does not depend on the Platform, provided you and your co-participants have kept your keys and the Vault Specification and are able to use third-party wallet software.',
  },
  {
    title: 'Regulatory risk.',
    body:
      'Laws applying to Bitcoin and to multisignature arrangements may change, and may restrict or prohibit use of the Platform in your jurisdiction or require us to change or withdraw the service.',
  },
  {
    title: 'Tax.',
    body:
      'Transactions involving Bitcoin may have tax consequences. You are responsible for determining and meeting your tax obligations and for keeping your own records.',
  },
];


/**
 * A link out of the SPA origin. Opens in a new tab for the same reason the
 * footer's Privacy link does (`layout.tsx`): the session JWT is in-memory, so
 * navigating this tab away would sign the reader out mid-session.
 *
 * The hrefs below are deliberately the canonical production URLs the legal
 * text names, not runtime-shimmed staging ones — the clauses assert where the
 * *published* document and policy live.
 */
function Ext({ href, children }: { href: string; children: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

const TERMS_URL = 'https://wrap.cobault.io/platform-terms';
const PRIVACY_URL = 'https://cobault.io/privacy';

export function PlatformTerms() {
  return (
    <div class="platform-terms">
      <h1 class="platform-terms-title">Cobault Platform Terms</h1>
      <p class="platform-terms-version">{VERSION_MARKER}</p>
      <p class="platform-terms-browsewrap">
        Use of this platform implies acceptance of these Cobault Platform
        Terms.
      </p>

      <h2>1. About these Terms</h2>
      <p>
        <strong>1.1</strong> These Platform Terms ("Terms") are an agreement
        between you and Bluemetal Services Pty Ltd (ACN 701 330 627)
        ("Cobault", "we", "us" or "our"), the company that operates Cobault:
        the web application at{' '}
        <Ext href="https://wrap.cobault.io">wrap.cobault.io</Ext> and the
        informational website at{' '}
        <Ext href="https://cobault.io">cobault.io</Ext> (together, the
        "Platform"). Bluemetal Services Pty Ltd is an Australian proprietary
        company and a wholly owned subsidiary of Chain Capital Technologies
        Ltd, a company registered in England and Wales.
      </p>
      <p>
        <strong>1.2</strong> These Terms govern your use of the Platform to
        create, join and operate Shared Vaults. Our Privacy Policy, published
        at <Ext href={PRIVACY_URL}>cobault.io/privacy</Ext>, explains how we
        handle personal information and forms part of these Terms.
      </p>
      <p>
        <strong>1.3</strong> The Platform offers standard Shared Vaults only.
        A standard Shared Vault is one in which every signing key is generated
        and held by the participants. Cobault does not hold a key in any
        Shared Vault. Vault configurations in which Cobault or any
        Cobault-operated service holds a co-signing key are not offered, and
        nothing in these Terms entitles you to such a feature.
      </p>
      <p>
        <strong>1.4</strong> Yield, strategy or other investment products are
        not offered under these Terms. If any such product is made available
        in future it will be offered under separate terms by the relevant
        product provider, and those terms will identify the provider and the
        risks specific to that product.
      </p>
      <p>
        <strong>1.5</strong> You accept these Terms by using the Cobault web
        application at{' '}
        <Ext href="https://wrap.cobault.io">wrap.cobault.io</Ext>, including by
        creating an account, creating a Shared Vault or accepting an invitation
        to join one. Visiting our informational website does not by itself
        constitute acceptance. Where the web application asks you to confirm
        your acceptance, we record the version you accepted and the time of
        acceptance. Each version of these Terms carries a version number and
        an effective date, and the current version is always available at{' '}
        <Ext href={TERMS_URL}>wrap.cobault.io/platform-terms</Ext>.
      </p>
      <p>
        <strong>1.6</strong> If you do not agree to these Terms, do not use
        the Platform. Because Cobault holds no keys, declining these Terms or
        closing your account does not affect any Bitcoin held in a Shared
        Vault you already participate in (see clause 14).
      </p>

      <h2>2. What the Platform is, and what it is not</h2>
      <p>
        <strong>2.1</strong> The Platform is non-custodial coordination
        software. It helps a group of people create a Bitcoin multisignature
        wallet that requires m of n signatures to spend (a "Shared Vault"),
        exchange the public information needed to construct it, and coordinate
        proposals and signatures for transactions from it. The Platform also
        displays vault balances and transaction history derived from the
        public Bitcoin blockchain.
      </p>
      <p>
        <strong>2.2</strong> Cobault does not generate, hold, store, back up,
        escrow or have access to any private key, seed phrase or recovery
        phrase belonging to any participant. Consequently Cobault cannot: move
        or transfer Bitcoin from a Shared Vault; sign or co-sign any
        transaction; reverse, cancel or amend a transaction once broadcast;
        freeze a vault or block a transaction that a quorum of participants
        has signed; recover a lost key or restore access to a vault; or return
        Bitcoin sent to a wrong address or to a vault whose quorum can no
        longer be assembled.
      </p>
      <p>
        <strong>2.3</strong> Bitcoin in a Shared Vault moves only when the
        required number of participants sign a transaction. If that number
        cannot be assembled, for any reason, the Bitcoin cannot move. This is
        a property of the Bitcoin network and of the configuration the
        participants chose, not a service that Cobault provides or can
        override.
      </p>
      <p>
        <strong>2.4</strong> Cobault is not a custodian, digital currency
        exchange, broker, dealer, payment service provider, deposit-taking
        institution, trustee or financial adviser. Cobault does not hold an
        Australian financial services licence and does not provide financial
        product advice. Nothing on the Platform is a recommendation to
        acquire, hold or dispose of Bitcoin. Bitcoin is not legal tender in
        Australia, is not a deposit, and is not covered by any deposit
        guarantee or investor compensation scheme.
      </p>
      <p>
        <strong>2.5</strong> The Bitcoin network is operated by third parties,
        not by Cobault. Confirmation times, network fees, mempool behaviour,
        chain reorganisations and protocol changes are outside our control.
      </p>
      <p>
        <strong>2.6</strong> Your Shared Vault does not depend on the
        Platform. From the vault page any participant can download the vault's
        complete specification, including its output descriptor and each
        participant's public key information (the "Vault Specification"). The
        Vault Specification, together with the participants' keys, is all that
        is needed to watch and spend from the vault using third-party wallet
        software without Cobault. You should download and securely retain the
        Vault Specification for every Shared Vault you participate in.
      </p>

      <h2>3. Eligibility and jurisdiction</h2>
      <p>
        <strong>3.1</strong> You must be at least 18 years old and have legal
        capacity to enter into these Terms. If you use the Platform on behalf
        of a company, trust or other entity, you warrant that you are
        authorised to bind that entity, and "you" includes that entity.
      </p>
      <p>
        <strong>3.2</strong> The Platform is offered only to persons who
        reside in Australia, Singapore, Hong Kong, the United Kingdom, a
        Member State of the European Union or the United States of America
        (the "Supported Jurisdictions"). The United States of America means
        the fifty States and the District of Columbia; its territories are
        not Supported Jurisdictions. We may add or remove jurisdictions from
        time to time; the current list is published on the Platform. By using
        the Platform you confirm that you reside in a Supported Jurisdiction.
        We do not ask you to declare your location and we rely on this
        confirmation. We apply technical restrictions, including restrictions
        based on the apparent location of your connection, and we may refuse,
        suspend or close an account where we reasonably believe that the
        confirmation is untrue.
      </p>
      <p>
        <strong>3.3</strong> You must not use a VPN, proxy or any other means
        to disguise your location in order to use the Platform from outside a
        Supported Jurisdiction. Doing so is a breach of these Terms.
      </p>
      <p>
        <strong>3.4</strong> You warrant that you are not, and are not acting
        on behalf of, a person or entity that is the subject of sanctions
        administered by Australia, the United Nations, the United Kingdom, the
        European Union or the United States of America, or by any other
        authority whose sanctions apply to us or to you; that you are not
        located in, or ordinarily resident in, a country or territory subject
        to comprehensive sanctions; and that you will not use the Platform to
        send Bitcoin to, or coordinate a vault with, any such person or
        entity. We may suspend an account under clause 13.3 where we
        reasonably believe this warranty is untrue.
      </p>
      <p>
        <strong>3.5</strong> You must not use the Platform if we have
        previously suspended or closed an account held by you for breach of
        these Terms.
      </p>

      <h2>4. Your account and your keys</h2>
      <p>
        <strong>4.1</strong> You create an account using one of the supported
        sign-in methods: an embedded wallet provided by a third-party sign-in
        service (MetaMask Embedded Wallets, operated by Web3Auth) using a
        social login, an email one-time code or an SMS one-time code; a
        recovery phrase that you generate on your own device and keep
        yourself; or a supported hardware wallet. Each method results in a
        seed that only you control. You must verify your email address when
        you register.
      </p>
      <p>
        <strong>4.2</strong> The Platform derives your vault signing keys and
        your Platform identity key from that seed on your device. Whichever
        sign-in method you use, the seed and the keys derived from it are
        yours. Cobault never receives them.
      </p>
      <p>
        <strong>4.3</strong> Before you can join a Shared Vault, and again
        before you sign to activate one, the Platform asks you to confirm that
        you have backed up your own signing key — your recovery phrase, or the
        sign-in method you use with Cobault — and that you understand that
        losing it could leave the vault unable to form a signing quorum. If
        you sign in with an embedded wallet, your access to your key depends
        on your continued access to that sign-in method, such as the social
        account, email address or phone number you used; losing it may mean
        losing your key permanently. If you lose your key we cannot recover
        it.
      </p>
      <p>
        <strong>4.4</strong> You are responsible for the security of your
        devices, your sign-in credentials, your recovery phrase and any
        hardware wallet. You must tell us promptly at{' '}
        <a href="mailto:support@cobault.io">support@cobault.io</a> if you
        become aware of unauthorised access to your account. Access to your
        account does not, by itself, give access to your keys, but it may
        expose information about your vaults and co-participants.
      </p>
      <p>
        <strong>4.5</strong> You may hold one account. You must provide
        accurate information and keep your contact details current. We limit
        each Shared Vault to a maximum number of invited participants
        (currently 10, in addition to the creator) and each account to a
        maximum number of active Shared Vaults (currently 10). The current
        limits are shown in the Platform. We may change them for vaults
        created after the change.
      </p>

      <h2>5. Creating and joining a Shared Vault</h2>
      <p>
        <strong>5.1</strong> <strong>Creation.</strong> The creator of a
        Shared Vault chooses the number of other participants and the number
        of signatures required to spend (m of n), and may label each
        participant slot. Once every participant has joined and the vault has
        been sealed, its participants, keys and signing threshold cannot be
        changed. To change them, a new Shared Vault must be created and the
        Bitcoin moved to it by a transaction signed by the required quorum of
        the existing vault.
      </p>
      <p>
        <strong>5.2</strong> <strong>Invitations.</strong> For each
        participant slot the Platform generates a claim link, which is shown
        to the creator once. Cobault does not send invitations. The creator is
        responsible for passing each link, through a channel of their own
        choosing, only to the person they intend to join. A claim link is a
        bearer credential: whoever holds it can claim the slot. Links expire
        after 7 days and can be used once. The creator can regenerate a link,
        which invalidates the previous one. When a slot is claimed the creator
        is notified, and the claimant's account email address is shown to all
        participants. You must not offer, give, request or accept any payment,
        reward or other inducement for sharing or accepting a claim link.
      </p>
      <p>
        <strong>5.3</strong> <strong>Joining.</strong> To join a Shared Vault
        you must sign in or create an account, confirm that you are joining
        under that account, confirm your key backup (clause 4.3) and accept
        these Terms. Your signing key for the vault is then generated on your
        device and you become a participant and keyholder. Before the vault
        becomes active, every participant is asked to check the list of
        participants and to acknowledge that lost keys cannot be recovered.
        You should only accept a claim link from a person you know and trust,
        and you should verify the participant list before you acknowledge it.
      </p>
      <p>
        <strong>5.4</strong> <strong>Participant visibility.</strong> Each
        participant in a Shared Vault can see the account email address,
        public key and signing activity of every other participant in that
        vault. Proposals, partial signatures and executed transactions are
        visible to all participants. Executed transactions are permanently and
        publicly recorded on the Bitcoin blockchain, and the transaction data
        reveals which participant keys signed.
      </p>
      <p>
        <strong>5.5</strong> <strong>Deposits.</strong> Once a Shared Vault is
        active, the Platform shows its deposit address and anyone may send
        Bitcoin to it. Do not send Bitcoin to a vault before it is active, to
        an address on any network other than Bitcoin mainnet, or to any
        address you have not verified. We cannot recover Bitcoin sent in
        error.
      </p>

      <h2>6. Transactions from a Shared Vault</h2>
      <p>
        <strong>6.1</strong> Any participant may propose a withdrawal from a
        Shared Vault by specifying a destination address and an amount. Only
        one withdrawal may be open on a vault at a time. The transaction is
        constructed on the Platform and executes only when the required number
        of participants have signed it with their own keys. Cobault does not
        sign.
      </p>
      <p>
        <strong>6.2</strong> Once the required signatures are collected the
        Platform assembles the final transaction and broadcasts it to the
        Bitcoin network. Any participant may also broadcast the finalised
        transaction independently. Once broadcast and confirmed, a Bitcoin
        transaction cannot be reversed by anyone, including Cobault.
      </p>
      <p>
        <strong>6.3</strong> The Platform does not verify that a destination
        address is the one you intend or that it belongs to the person you
        believe it does. Before signing you must independently verify the
        destination address and amount, ideally on a separate device or a
        hardware wallet display.
      </p>
      <p>
        <strong>6.4</strong> Any participant may cancel a withdrawal that has
        not yet been broadcast. Withdrawals do not expire automatically.
        Cancelling stops the Platform from broadcasting the transaction, but
        if the transaction has already been fully signed it remains a valid
        Bitcoin transaction until the funds it spends are spent by another
        transaction, and anyone holding a copy of it could broadcast it.
      </p>
      <p>
        <strong>6.5</strong> <strong>Network fees.</strong> Every transaction
        pays a fee to the Bitcoin network. The fee is set when the withdrawal
        is proposed and is paid from the vault. Fee estimates shown on the
        Platform are estimates only. A transaction with an insufficient fee
        may be delayed or may not confirm.
      </p>
      <p>
        <strong>6.6</strong> Cobault is not a participant in your Shared
        Vault, holds no key in it, and is not a party to any arrangement
        between the participants about how it is used. Any dispute between
        participants, including about whether a withdrawal should be signed,
        is a matter for the participants. Cobault cannot adjudicate,
        intervene, or compel or prevent any signature.
      </p>

      <h2>7. Fees</h2>
      <p>
        <strong>7.1</strong> Cobault does not charge a fee for creating or
        using a Shared Vault. Bitcoin network fees (clause 6.5) and any
        charges of your sign-in service, hardware wallet vendor or
        telecommunications carrier are your responsibility.
      </p>
      <p>
        <strong>7.2</strong> If we introduce a Platform fee we will publish it
        and give you at least 30 days' notice by email and in the Platform
        before it takes effect. A new fee will not apply to a Shared Vault
        created before the notice unless you agree to it. You may at any time
        download your Vault Specification and operate a Shared Vault outside
        the Platform without paying any Cobault fee.
      </p>

      {/* COB-513 (C4): the anchor the footer's "Risk Disclosure" link
          targets. Clause 8 IS the published risk disclosure — it carries
          every risk C4 enumerates for the p2p launch scope, and
          `PLATFORM_TERMS_RISKS` renders it here and on both consent
          surfaces from one array, so a standalone page would only add a
          third text that could drift. The id is what lets the footer name
          the disclosure without duplicating it. Do not rename it. */}
      <h2 id="risks">8. Risks you accept</h2>
      <p>
        <strong>8.1</strong> Shared Vaults carry risks that you must
        understand before you create or join one. By accepting these Terms you
        acknowledge that you have read and understood the following, and that
        you accept these risks:
      </p>
      <ul class="platform-terms-list">
        {PLATFORM_TERMS_RISKS.map((risk) => (
          <li key={risk.title}>
            <strong>{risk.title}</strong> {risk.body}
          </li>
        ))}
      </ul>
      <p>
        <strong>8.2</strong> You are responsible for deciding whether a Shared
        Vault is suitable for you. Cobault does not assess suitability. You
        should consider obtaining independent professional advice.
      </p>

      <h2>9. Acceptable use</h2>
      <p>
        <strong>9.1</strong> You must not use the Platform, or any Shared
        Vault created through it:
      </p>
      <ul class="platform-terms-list">
        <li>
          for any unlawful purpose, including to hold or move the proceeds of
          crime, to finance terrorism or to evade sanctions;
        </li>
        <li>
          to misrepresent your identity, residence or authority, or to
          circumvent the jurisdiction restriction, the participant or vault
          limits, or the claim-link controls;
        </li>
        <li>
          to send unsolicited, deceptive or harassing claim links, or to offer
          or accept inducements for claim links;
        </li>
        <li>
          to interfere with, probe, overload or gain unauthorised access to
          the Platform or its infrastructure, or to introduce malicious code;
        </li>
        <li>
          to copy, modify, reverse engineer or create derivative works of the
          Platform, except to the extent permitted by law or by an applicable
          open-source licence;
        </li>
        <li>
          to provide a regulated financial, custody or exchange service to
          others without the licences that service requires; or
        </li>
        <li>
          in a way that infringes the rights of any person or brings Cobault
          into disrepute.
        </li>
      </ul>
      <p>
        <strong>9.2</strong> We may suspend or close an account used in breach
        of this clause in accordance with clause 13.3.
      </p>

      <h2>10. Intellectual property and licence</h2>
      <p>
        <strong>10.1</strong> The Platform, its software, design, text and
        trade marks (including "Cobault") are owned by Cobault or its
        licensors. We grant you a limited, non-exclusive, non-transferable,
        revocable licence to use the Platform in accordance with these Terms.
        Open-source components are licensed under their own terms, which
        prevail for those components.
      </p>
      <p>
        <strong>10.2</strong> Your keys, your Vault Specification and the
        Bitcoin in your Shared Vaults are yours. Nothing in these Terms
        restricts your right to download the Vault Specification and use it
        with any software you choose.
      </p>
      <p>
        <strong>10.3</strong> You retain ownership of any content or data you
        submit. You grant us a licence to store and process it as needed to
        provide the Platform to you and your co-participants, in accordance
        with our Privacy Policy.
      </p>
      <p>
        <strong>10.4</strong> If you give us feedback or suggestions we may
        use them without obligation to you.
      </p>

      <h2>11. Third-party services</h2>
      <p>
        <strong>11.1</strong> The Platform relies on services we do not
        control, including your sign-in service provider, hardware wallet
        vendors, the Bitcoin network, and any third-party wallet software you
        use with your Vault Specification. Those services are subject to their
        own terms and privacy policies. We are not responsible for their
        availability, security or conduct.
      </p>
      <p>
        <strong>11.2</strong> One-time codes used for sign-in are sent by the
        sign-in service provider, not by Cobault. Standard message and data
        charges may apply.
      </p>

      <h2>12. Privacy</h2>
      <p>
        <strong>12.1</strong> Our Privacy Policy at{' '}
        <Ext href={PRIVACY_URL}>cobault.io/privacy</Ext> explains what
        personal information we collect, how we use it, who we share it with,
        how long we keep it and how to exercise your rights. Questions and
        privacy complaints may be sent to{' '}
        <a href="mailto:privacy@cobault.io">privacy@cobault.io</a>.
      </p>
      <p>
        <strong>12.2</strong> Information recorded on the Bitcoin blockchain,
        including vault addresses and transaction data, is public and
        permanent and cannot be deleted by Cobault. Our Privacy Policy
        explains how we handle the link between that information and you.
      </p>

      <h2>13. Availability, changes to the Platform and suspension</h2>
      <p>
        <strong>13.1</strong> We aim to keep the Platform available but do not
        guarantee that it will be uninterrupted or error-free. We may suspend
        the Platform for maintenance, security or operational reasons, and
        will give reasonable advance notice where practicable.
      </p>
      <p>
        <strong>13.2</strong> We may change the Platform from time to time. We
        will not make a change that removes your ability to download your
        Vault Specification, or to view the information needed to operate your
        Shared Vault outside the Platform, without giving you at least 30
        days' notice, unless the change is required by law or is needed to
        address a security risk.
      </p>
      <p>
        <strong>13.3</strong> We may suspend or restrict your account only
        where: you are in material breach of these Terms; we are required to
        do so by law, a court order or a regulator; we reasonably believe your
        account or the Platform is at risk of compromise; or we reasonably
        believe you do not reside in a Supported Jurisdiction or are otherwise
        ineligible. Where the law permits we will tell you the reason and give
        you an opportunity to respond. We will lift a suspension when the
        reason for it has been resolved.
      </p>
      <p>
        <strong>13.4</strong> Suspension of your account does not, and cannot,
        affect the Bitcoin in any Shared Vault. Your keys remain yours, and
        any Shared Vault remains operable by its participants outside the
        Platform using the Vault Specification.
      </p>
      <p>
        <strong>13.5</strong> If we decide to discontinue the Platform we will
        give you at least 90 days' notice by email and in the Platform, keep
        the Vault Specification download available throughout that period, and
        publish guidance on operating your Shared Vaults with third-party
        software.
      </p>

      <h2>14. Term and termination</h2>
      <p>
        <strong>14.1</strong> You may close your account at any time by
        emailing <a href="mailto:support@cobault.io">support@cobault.io</a>.
        Closing your account does not remove you as a keyholder of any Shared
        Vault: your co-participants will still need your signature where the
        configuration requires it. Before closing, download the Vault
        Specification for each Shared Vault and consider whether Bitcoin
        should be moved to a new vault.
      </p>
      <p>
        <strong>14.2</strong> We may terminate these Terms and close your
        account on the grounds in clause 13.3 where the breach or issue is not
        remedied within 14 days of our notice (or immediately where it cannot
        be remedied or the law requires), or otherwise on 30 days' written
        notice.
      </p>
      <p>
        <strong>14.3</strong> Clauses 2, 6.6, 8, 10, 15, 16, 19 and 20, and
        any other clause that by its nature should survive, continue after
        termination.
      </p>

      <h2>15. Consumer guarantees, warranties and liability</h2>
      <p>
        <strong>15.1</strong> <strong>Australian Consumer Law.</strong> Our
        services come with guarantees that cannot be excluded under the
        Australian Consumer Law. Nothing in these Terms excludes, restricts or
        modifies any consumer guarantee, right or remedy that cannot lawfully
        be excluded, restricted or modified. Where the law allows us to limit
        our liability for failure to comply with a consumer guarantee in
        relation to services, our liability is limited to supplying the
        services again or paying the cost of having them supplied again.
      </p>
      <p>
        <strong>15.2</strong> Except as set out in clause 15.1 and to the
        extent permitted by law, the Platform is provided "as is" and we make
        no other warranty about it.
      </p>
      <p>
        <strong>15.3</strong> To the extent permitted by law, we are not
        liable for loss arising from: loss, theft or compromise of your keys,
        recovery phrase, sign-in method, devices or credentials; the acts or
        omissions of your co-participants; the Bitcoin network, including
        fees, delays, reorganisations or protocol changes; the third-party
        services described in clause 11; a transaction you or your
        co-participants signed, including one sent to a wrong address; changes
        in the value of Bitcoin; your breach of these Terms; or any event
        outside our reasonable control. To the extent permitted by law, we are
        not liable for indirect or consequential loss, loss of profit or loss
        of opportunity.
      </p>
      <p>
        <strong>15.4</strong> To the extent permitted by law, our total
        liability to you under or in connection with these Terms in any
        12-month period is limited to the greater of A$1,000 and the Platform
        fees you paid us in that period.
      </p>
      <p>
        <strong>15.5</strong> Nothing in these Terms limits or excludes our
        liability for fraud, for death or personal injury caused by our
        negligence, or for any liability that cannot be limited or excluded by
        law.
      </p>
      <p>
        <strong>15.6</strong> If you are a consumer resident in Singapore or
        Hong Kong, nothing in these Terms excludes or restricts any right you
        have under the consumer protection laws of that jurisdiction that
        cannot be excluded or restricted by agreement.
      </p>
      <p>
        <strong>15.7</strong> <strong>United Kingdom and European Union.</strong> If
        you are a consumer resident in the United Kingdom or in a Member
        State of the European Union: (a) nothing in these Terms excludes or
        restricts any right you have under the consumer protection laws of the
        United Kingdom or of that Member State that cannot be excluded or
        restricted by agreement, including your rights in respect of digital
        content and digital services and your right to bring proceedings in
        your own courts; (b) the information we are required to give you
        before you are bound — who we are, what the Platform does, that it is
        supplied free of charge for standard shared vaults, how long these
        Terms run and how you end them, and how to complain — is set out in
        clauses 1, 2, 7, 14 and 18 and in the identity block at the foot of
        this page; (c) because the Platform is a digital service that begins
        as soon as you register, you acknowledge that you have asked us to
        start supplying it during the 14-day cancellation period and that you
        may end your use at any time under clause 14.1 without charge — since
        no fee is payable for standard shared vaults, no refund arises, and
        because we hold no keys, ending your use does not affect the Bitcoin
        in your Shared Vaults; and (d) the European Commission's online
        dispute resolution platform is no longer available; the complaint
        routes in clause 18 apply.
      </p>
      <p>
        <strong>15.8</strong> <strong>United States of America.</strong> If
        you are a consumer resident in the United States of America, nothing
        in these Terms excludes or restricts any right you have under the
        consumer protection laws of the United States or of your State that
        cannot be excluded or restricted by agreement, and clause 15.3 applies
        only to the extent permitted by those laws. Some States do not allow
        the exclusion of implied warranties or the limitation of certain
        damages, so parts of clauses 15.2 to 15.4 may not apply to you.
      </p>

      <h2>16. Your indemnity</h2>
      <p>
        <strong>16.1</strong> You indemnify us against any third-party claim,
        loss or expense (including reasonable legal costs) to the extent it
        arises from your breach of these Terms or your unlawful use of the
        Platform. This indemnity does not apply to the extent the loss is
        caused by our negligence, our breach of these Terms or our breach of
        law.
      </p>

      <h2>17. Changes to these Terms</h2>
      <p>
        <strong>17.1</strong> We may update these Terms. Each version carries
        a version number and an effective date and is published at{' '}
        <Ext href={TERMS_URL}>wrap.cobault.io/platform-terms</Ext>.
      </p>
      <p>
        <strong>17.2</strong> For a material change we will give you at least
        30 days' notice by email to your account address and in the Platform,
        and we will ask you to accept the new version before you next create
        or join a Shared Vault. A change required by law, or needed to address
        a security risk, may take effect on shorter notice.
      </p>
      <p>
        <strong>17.3</strong> If you do not agree to a change you may stop
        using the Platform and close your account under clause 14.1. Because
        Cobault holds no keys, doing so does not affect the Bitcoin in your
        Shared Vaults.
      </p>

      <h2>18. Complaints and support</h2>
      <p>
        <strong>18.1</strong> Support requests and complaints may be sent to{' '}
        <a href="mailto:support@cobault.io">support@cobault.io</a>. We will
        acknowledge a complaint within 5 business days and aim to resolve it
        within 30 days. If we need longer we will tell you why and when you
        can expect a response.
      </p>
      <p>
        <strong>18.2</strong> Privacy complaints may be sent to{' '}
        <a href="mailto:privacy@cobault.io">privacy@cobault.io</a>. If you are
        not satisfied with our response you may complain to the Office of the
        Australian Information Commissioner, the Personal Data Protection
        Commission of Singapore, the Office of the Privacy Commissioner for
        Personal Data of Hong Kong, or the privacy regulator in your
        jurisdiction.
      </p>
      <p>
        <strong>18.3</strong> Nothing in these Terms prevents you from making
        a complaint to a consumer protection regulator or bringing a claim in
        a court or tribunal.
      </p>

      <h2>19. Governing law</h2>
      <p>
        <strong>19.1</strong> These Terms are governed by the laws of New
        South Wales, Australia. You and we submit to the non-exclusive
        jurisdiction of the courts of New South Wales and the courts entitled
        to hear appeals from them.
      </p>
      <p>
        <strong>19.2</strong> If you are a consumer resident in another
        Supported Jurisdiction, nothing in clause 19.1 deprives you of the
        protection of laws of that jurisdiction that cannot be excluded by
        agreement, or of your right to bring proceedings in the courts of that
        jurisdiction where the law gives you that right.
      </p>

      <h2>20. General</h2>
      <p>
        <strong>20.1</strong> These Terms, together with the Privacy Policy,
        are the entire agreement between you and us about the Platform.
        Nothing in this clause limits liability for fraudulent
        misrepresentation.
      </p>
      <p>
        <strong>20.2</strong> If any part of these Terms is unenforceable, the
        rest continues in effect and the unenforceable part is severed or read
        down to the minimum extent necessary.
      </p>
      <p>
        <strong>20.3</strong> We may assign these Terms to a related body
        corporate or to a purchaser of the Platform business on notice to you.
        You may not assign your rights under these Terms.
      </p>
      <p>
        <strong>20.4</strong> A failure by either party to enforce a right is
        not a waiver of it.
      </p>
      <p>
        <strong>20.5</strong> We may give you notices by email to your account
        address or through the Platform. You may give us notice by email to{' '}
        <a href="mailto:support@cobault.io">support@cobault.io</a> or by post
        to Bluemetal Services Pty Ltd, c/o Flinders Financial, Level 2, 179 St
        Georges Terrace, Perth WA 6000, Australia.
      </p>
      <p>
        <strong>20.6</strong> Neither party is liable for failure to perform
        caused by events beyond its reasonable control, except that this does
        not excuse your obligations under clauses 3 and 9.
      </p>
      <p>
        <strong>20.7</strong> These Terms are written in English. Any
        translation is provided for convenience and the English text prevails.
      </p>
      <p>
        <strong>20.8</strong> Nothing in these Terms creates a partnership,
        agency, trust, fiduciary relationship or joint venture between you and
        us, or between us and any other participant in a Shared Vault.
      </p>

      <h2>21. Definitions</h2>
      <table class="platform-terms-definitions">
        <thead>
          <tr>
            <th scope="col">Term</th>
            <th scope="col">Meaning</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Bitcoin</th>
            <td>the native asset of the Bitcoin mainnet network.</td>
          </tr>
          <tr>
            <th scope="row">claim link</th>
            <td>
              the single-use link generated by the Platform for a participant
              slot, by which a person joins a Shared Vault (clause 5.2).
            </td>
          </tr>
          <tr>
            <th scope="row">Cobault, we, us, our</th>
            <td>Bluemetal Services Pty Ltd (ACN 701 330 627).</td>
          </tr>
          <tr>
            <th scope="row">Platform</th>
            <td>
              the Cobault web application at{' '}
              <Ext href="https://wrap.cobault.io">wrap.cobault.io</Ext>, the
              informational website at{' '}
              <Ext href="https://cobault.io">cobault.io</Ext>, and associated
              services operated by us. Clause 1.5 states what constitutes
              acceptance of these Terms.
            </td>
          </tr>
          <tr>
            <th scope="row">Privacy Policy</th>
            <td>
              our privacy policy published at{' '}
              <Ext href={PRIVACY_URL}>cobault.io/privacy</Ext>, as updated
              from time to time.
            </td>
          </tr>
          <tr>
            <th scope="row">Shared Vault</th>
            <td>
              a Bitcoin multisignature wallet created through the Platform in
              which all signing keys are held by the participants.
            </td>
          </tr>
          <tr>
            <th scope="row">Supported Jurisdictions</th>
            <td>
              the jurisdictions listed in clause 3.2, as updated on the
              Platform from time to time.
            </td>
          </tr>
          <tr>
            <th scope="row">Vault Specification</th>
            <td>
              the output descriptor and associated data, downloadable from the
              vault page, that together with the participants' keys fully
              describes a Shared Vault and allows it to be operated with
              third-party software.
            </td>
          </tr>
          <tr>
            <th scope="row">you, your</th>
            <td>the person or entity using the Platform.</td>
          </tr>
        </tbody>
      </table>

      <hr class="platform-terms-rule" />
      <div class="platform-terms-colophon">
        <p>
          Bluemetal Services Pty Ltd (ACN 701 330 627), operator of the
          Cobault platform.
        </p>
        <p>
          Registered office: Level 2, 179 St Georges Terrace, Perth WA 6000,
          Australia.
        </p>
        <p>
          Support and complaints:{' '}
          <a href="mailto:support@cobault.io">support@cobault.io</a> · Privacy:{' '}
          <a href="mailto:privacy@cobault.io">privacy@cobault.io</a>
        </p>
        <p class="platform-terms-colophon-version">{VERSION_MARKER}</p>
      </div>
    </div>
  );
}
